import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/user.selectors';
import { bannerContext } from '../../state/BannerContextProvider';
import { routes } from '../../constants';
import { Link } from 'found';
import config from 'config';
import { isFeatureEnabled } from '../../utils/feature';
import { Box } from '@mui/material';

// Custom colours from previous design system
const customColours = {
  popGreen: '#00bf6f',
  white: '#fff',
  battleGrey: '#727e82',
};

const commonLinkStyles = {
  padding: '0 8px',
  textAlign: 'center',
  flexShrink: 0,
  '@media (min-width: 768px)': {
    flexDirection: 'row',
    fontSize: '16px',
    marginRight: '16px',
    padding: '4px 16px',
    textAlign: 'left',
  },
};

const primaryLinkStyles = {
  ...commonLinkStyles,
  backgroundColor: customColours.white,
  borderRadius: '4px',
  color: customColours.popGreen,
  fontWeight: '700',
  textDecoration: 'none',
  '&:hover': {
    boxShadow: `${customColours.battleGrey} 0 0 0 2px`,
    color: customColours.popGreen,
  },
};

const secondaryLinkStyles = {
  ...commonLinkStyles,
  color: customColours.white,
  textDecoration: 'underline',
  width: '140px',
  '@media (min-width: 768px)': {
    width: '160px',
  },
  '&:hover': {
    color: customColours.white,
    fontWeight: '700',
    textDecoration: 'underline',
  },
};

const BannerContainer: React.FC = ({ children }) => {
  return (
    <Box sx={{
      backgroundColor: customColours.popGreen,
      color: customColours.white,
      flexDirection: 'column',
      textAlign: 'center',
      fontSize: '12px',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      paddingRight: '1.5rem',
      paddingLeft: '1.5rem',
      display: 'flex',
      '@media (min-width: 768px)': {
        flexDirection: 'row',
        textAlign: 'left',
        fontSize: '16px',
      },
    }}
    >
      {children}
    </Box>
  );
};

const BannerInnerContainer: React.FC = ({ children }) => {
  return (
    <Box sx={{
      flexGrow: 1,
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
    }}>
      {children}
    </Box>
  );
};

const TimedUpgradeBanner: React.FC = () => {
  const { t } = useTranslation();
  const activeDates = config.features['upgradeBanner'].activeDates;
  if (!activeDates) {
    return <></>;
  }
  
  // If there are no active dates provided, then the banner should be visible
  const today: number = new Date().setHours(0, 0, 0, 0);
  const startDate: number = new Date(activeDates.startDate).setHours(0, 0, 0, 0);
  const earlyBirdEnd: number = new Date(activeDates.midDate).setHours(0, 0, 0, 0);
  const endDate: number = new Date(activeDates.endDate).setHours(0, 0, 0, 0);
  
  const isActiveDatesCorrect: boolean = today >= startDate && today <= endDate;

  if (!isActiveDatesCorrect) {
    return <></>;
  }

  // Distinction of two/three upgrade banner ranges
  const isTodayEarlyBird = today <= earlyBirdEnd;
  let promoCodeRegular, promoCodeTrainee, i18nKey;
  if (isTodayEarlyBird) {
    promoCodeRegular = 'klassenheld20';
    promoCodeTrainee = 'klassenheld20-ref';
    i18nKey = '20% discount on meinUnterricht: get the full power for your new school year.';
  } else {
    promoCodeRegular = 'endspurt30';
    promoCodeTrainee = 'endspurt30-ref';
    i18nKey = 'Just this week: 30% discount. With AI and top content through the new school year!';
  }

  const pathname = `/${routes.upgradePayment.root}`;

  return (
    <BannerContainer>
      <BannerInnerContainer>
        <Trans i18nKey={i18nKey} key={1}>
          <strong>🎁 % Discount:</strong> For a successful school year.
        </Trans>
        {/* t('Sorry. We are having server problems. We are working on a solution.') */}
      </BannerInnerContainer>
      <Box sx={{
        alignItems: 'center',
        display: 'flex',
        flexGrow: 0,
        justifyContent: 'center',
        '@media (min-width: 768px)': {
          justifyContent: 'end',
        },
      }}>
        <Box
          component={Link}
          to={{
            pathname,
            query: {
              licenceId: '1320',
              promoCode: promoCodeRegular
            }
          }}
          sx={primaryLinkStyles}
        >
          {t('Go to teacher discount')}
        </Box>
        <Box
          component={Link}
          to={{
            pathname,
            query: {
              licenceId: '410',
              promoCode: promoCodeTrainee
            }
          }}
          sx={secondaryLinkStyles}
        >
          {t('Go to trainee discount')}
        </Box>
      </Box>
    </BannerContainer>
  );

};

const MaintainenceBanner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <BannerContainer>
      <BannerInnerContainer>
        {t('Sorry. We are having server problems. We are working on a solution.')}
      </BannerInnerContainer>
    </BannerContainer>
  );
};

const UpgradeBanner: React.FC = () => {
  const { bannerIsOpen } = useContext(bannerContext);
  const user = useSelector(selectUser);
  const validUser = user && !user.isSchoolUser && !user.isSchoolAdmin && !user.isWalkIn;
  const shouldShowBanner = isFeatureEnabled('upgradeBanner') && validUser;

  const showMaintainenceBanner = false;
  
  if (showMaintainenceBanner) {
    return <MaintainenceBanner />;
  }
  
  const isVisible = bannerIsOpen && shouldShowBanner;

  if (!isVisible) {
    return null;
  }

  return <TimedUpgradeBanner />;
};

export default UpgradeBanner;
